import { useState, useContext, useEffect, useMemo } from 'react';
import {
  Button,
  CardMedia,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { DataGrid, DataGridProps, GridColDef, getGridDateOperators, gridClasses } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import { useAppDispatch } from 'store/hooks';
import {
  getUnassignedConversations,
  getAssignedConversations,
  getVMAStatus,
  getConversationCount,
  getVMANames,
  assignConversationToOthers,
  getConversationGroupDetails,
  joinConversation,
  getGroupMembers
} from 'store/slices/secureMessageSlice';
import COLORS from 'theme/colors';
import { AuthContext } from 'utils/AuthContext';
import online from 'assets/images/online1.svg';
import offline from 'assets/images/offline.svg';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ROUTE } from 'route/RouteEnums';
import chat from 'assets/images/chat.svg';
import CustomDataGrid from 'utils/customDataGrid';
import PopupMenu from 'utils/PopupMenu';
import { getDefaultLandingPage } from 'utils/navigationUtils';

import PopupGroupMembers from 'utils/PopupGroupMembers';

const ConversationList = () => {
  const [conversations, setConversations] = useState<any[] | null>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [conversationCount, setConversationCount] = useState<any>('');
  const [vmaStatus, setVMAStatus] = useState<any>();
  const [vamNames, setVMANames] = useState<any>();
  const [selectedVMA, setSelectedVMA] = useState('');
  const [selectedRow, setSelectedRow] = useState('');
  const [open, setOpen] = useState<any>();
  const [groupDetails, setGroupDetails] = useState<any>(null);
  const pageKey = 'ConversationList';
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const [membersAnchorEl, setMembersAnchorEl] = useState<HTMLElement | null>(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [members, setMembers] = useState<string[]>([]);
  const handleTooltipClose = () => {
    setOpen(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveRowId(null);
  };

  const handleMenuOpen = async (e: any, row: any) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setActiveRowId(row.patientId);
    setLoading(true);
    // 🔵 Load your API data here
    const response = await handleTooltipOpen(e, row.patientId);
    setLoading(false);
  };

  const getUnassignedConversationsList = async () => {
    setLoading(true);
    await dispatch(getUnassignedConversations({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setConversations(response?.data);
          setLoading(false);
        }
      });
  };

  const getAssignedConversationsList = async () => {
    setLoading(true);
    await dispatch(getAssignedConversations({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setConversations(response?.data);
          setLoading(false);
        }
      });
  };
  const handleViewMembers = async (e: any, groupId: number) => {
    e.preventDefault();
    e.stopPropagation();

    setMembers([]);
    setMembersAnchorEl(e.currentTarget);

    try {
      setMembersLoading(true);
      const response = await dispatch(getGroupMembers(groupId)).unwrap();

      if (response?.status === 200) {
        setMembers(response?.data ?? []);
        setMembersLoading(false);
      }
    } catch (error) {
      console.error('Error fetching group members:', error);
      toast('Failed to load members', {
        type: 'error',
        icon: true,
      });
    }finally {
      setMembersLoading(false);
    }
  };

  const handleMembersClose = () => {
    setMembersAnchorEl(null);
    setMembers([]);
  };

  useEffect(() => {
    if (!user?.permissions.includes('CONVERSATIONS_INSIGHT')) {
      navigate(getDefaultLandingPage());
    }
    getAssignedConversationsList();
    loadConversationCount();
    dispatch(getVMAStatus({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const data = {
            online:
              // eslint-disable-next-line array-callback-return
              response.data
                .map((x: any) => {
                  if (x.status === 'available') {
                    return x.count;
                  }
                })
                .filter((ele: any) => ele),
            // eslint-disable-next-line array-callback-return
            offline: response.data
              .map((x: any) => {
                if (x.status === 'offline') {
                  return x.count;
                }
              })
              .filter((ele: any) => ele),
          };
          setVMAStatus(data);
        }
      });

    dispatch(getVMANames({}))
      .unwrap()
      .then((res: any) => {
        if (res.status === 200) {
          setVMANames(res?.data);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConversationCount = () => {
    dispatch(getConversationCount({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const data = {
            assigned:
              // eslint-disable-next-line array-callback-return
              response.data
                .map((x: any) => {
                  if (x.status === 'in-progress') {
                    return x.count;
                  }
                })
                .filter((ele: any) => ele),
            // eslint-disable-next-line array-callback-return
            unassigned: response.data
              .map((x: any) => {
                if (x.status === 'open') {
                  return x.count;
                }
              })
              .filter((ele: any) => ele),
          };
          setConversationCount(data);
        }
      });
  };
  const handleVMAChange = (e: any, params: any) => {
    setSelectedRow(params?.row?.groupId);
    setSelectedVMA(e.target.value);
  };

  const handleAssignConversation = (params: any) => {
    const data = {
      groupId: params.row.groupId,
      id: selectedVMA,
    };
    dispatch(assignConversationToOthers(data))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          toast('Conversation Assigned', {
            type: 'success',
            icon: true,
          });
          getUnassignedConversationsList();
          loadConversationCount();
        } else {
          toast('Something went wrong', {
            type: 'error',
            icon: true,
          });
        }
      });
  };

  const openPatientChart = (patientId: string | number) => {
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${patientId}`, '_blank');
  };

  const renderPatientNameCell = (params: any) => (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <Tooltip title='Patient Chart'>
        <IconButton
          size='small'
          aria-label='Patient Chart'
          onClick={(event) => {
            event.stopPropagation();
            openPatientChart(params.row.patientId);
          }}
        >
          <PersonIcon fontSize='small' sx={{ color: '#3366FF' }} />
        </IconButton>
      </Tooltip>
      <span>{params?.row?.patientName}</span>
    </span>
  );

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'action',
      headerName: 'Action',
      width: 80,
      hideable: false,
      filterable: false,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <>
            <CardMedia
              src={chat}
              component='img'
              sx={{ width: 24, cursor: 'pointer' }}
              onClick={(e) => handleMenuOpen(e, params.row)}
            />
          </>
        );
      },
    },
    {
      field: 'guardianName',
      headerName: 'Guardian Name',
      flex: 200,
      hideable: false,
    },
    {
      field: 'patientName',
      headerName: 'Patient Name',
      flex: 200,
      hideable: false,
      renderCell: renderPatientNameCell,
    },
    {
      field: 'assignedTo',
      headerName: 'Assigned To',
      flex: 200,
      hideable: false,
      renderCell: (params: any) => {
        return (
          <>
            {selectedTab === 0 ? (
              params?.row?.assignedTo
            ) : (
              <>
                <TextField
                  id='assignTo'
                  variant='outlined'
                  fullWidth
                  select
                  label='Select VMA'
                  onChange={(e: any) => handleVMAChange(e, params)}
                  value={selectedRow === params?.row?.groupId ? selectedVMA : ''}
                  sx={{ minWidth: '120px' }}
                >
                  {vamNames?.length &&
                    vamNames.map((v: any) => {
                      return (
                        <MenuItem key={v.firstName + v.lastName} value={v.userId}>
                          {v.firstName + ' ' + v.lastName}
                        </MenuItem>
                      );
                    })}
                </TextField>
                <Button
                  variant='text'
                  disabled={selectedRow === params?.row?.groupId ? false : true}
                  onClick={() => handleAssignConversation(params)}
                >
                  Assign
                </Button>
              </>
            )}
          </>
        );
      },
    },
    {
      field: 'type',
      headerName: 'Category',
      flex: 200,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      flex: 200,
      type: 'date',
      filterOperators: getGridDateOperators().filter(
        (operator) =>
          operator.value === 'isNotEmpty' ||
          operator.value === 'isEmpty' ||
          operator.value === 'onOrBefore' ||
          operator.value === 'onOrAfter' ||
          operator.value === 'after' ||
          operator.value === 'not' ||
          operator.value === 'is'
      ),
      valueFormatter: (params: any) => dayjs(params?.row?.startDate).format('MM/DD/YYYY'),
      valueGetter: (params: any) => dayjs(params?.row?.startDate).format('MM/DD/YYYY'),
      renderCell: (params: any) => {
        return <>{params?.row?.startDate ? dayjs(params?.row?.startDate).format('MM/DD/YYYY') : '-'}</>;
      },
    },
    {
      field: 'viewMembers',
      headerName: 'Members',
      flex: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <Button
            variant='text'
            size='small'
            type='button'
            sx={{
              textTransform: 'none',
              minWidth: 'auto',
              padding: 0,
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => handleViewMembers(e, params.row.groupId)}
          >
            View Members
          </Button>
        );
      },
    },
  ], [selectedTab, selectedRow, selectedVMA, vamNames]);

  const unAssignColumns = useMemo<GridColDef[]>(() =>  [
    {
      field: 'guardianName',
      headerName: 'Guardian Name',
      flex: 200,
      hideable: false,
    },
    {
      field: 'patientName',
      headerName: 'Patient Name',
      flex: 200,
      hideable: false,
      renderCell: renderPatientNameCell,
    },
    {
      field: 'assignedTo',
      headerName: 'Assigned To',
      hideable: false,
      flex: 200,
      renderCell: (params: any) => {
        return (
          <>
            {selectedTab === 0 ? (
              params?.row?.assignedTo
            ) : (
              <>
                <TextField
                  id='assignTo'
                  variant='outlined'
                  fullWidth
                  select
                  label='Select VMA'
                  onChange={(e: any) => handleVMAChange(e, params)}
                  value={selectedRow === params?.row?.groupId ? selectedVMA : ''}
                  sx={{ minWidth: '120px' }}
                >
                  {vamNames?.length &&
                    vamNames.map((v: any) => {
                      return (
                        <MenuItem key={v.firstName + v.lastName} value={v.userId}>
                          {v.firstName + ' ' + v.lastName}
                        </MenuItem>
                      );
                    })}
                </TextField>
                <Button
                  variant='text'
                  disabled={selectedRow === params?.row?.groupId ? false : true}
                  onClick={() => handleAssignConversation(params)}
                >
                  Assign
                </Button>
              </>
            )}
          </>
        );
      },
    },
    {
      field: 'type',
      headerName: 'Category',
      flex: 200,
      hideable: false,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      flex: 200,
      type: 'date',
      filterOperators: getGridDateOperators().filter(
        (operator) =>
          operator.value === 'isNotEmpty' ||
          operator.value === 'isEmpty' ||
          operator.value === 'onOrBefore' ||
          operator.value === 'onOrAfter' ||
          operator.value === 'after' ||
          operator.value === 'not' ||
          operator.value === 'is'
      ),
      valueFormatter: (params: any) => dayjs(params?.row?.startDate).format('MM/DD/YYYY'),
      valueGetter: (params: any) => dayjs(params?.row?.startDate).format('MM/DD/YYYY'),
      renderCell: (params: any) => {
        return <>{params?.row?.startDate ? dayjs(params?.row?.startDate).format('MM/DD/YYYY') : '-'}</>;
      },
    },
   ], [selectedTab, selectedRow, selectedVMA, vamNames]);

  const handleTabChange = (event: any, newValue: any) => {
    setConversations([]);
    setSelectedTab(newValue);
    if (newValue === 0) {
      getAssignedConversationsList();
    } else if (newValue === 1) {
      getUnassignedConversationsList();
    }
  };

  const handleTooltipOpen = (e: any, id: number) => {
    e.stopPropagation();
    setOpen(id);
    dispatch(getConversationGroupDetails(id))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setGroupDetails(response.data);
        }
      });
  };

  const handleJoinConversation = (g: any) => {
    dispatch(joinConversation(g.groupId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          navigate(`${ROUTE.ADMIN_MESSAGES}`);
        }
      });
  };
  return (
    <Container maxWidth='lg'>
      <Grid item xs={12}>
        <Typography
          variant='h5'
          position={'relative'}
          width={'fit-content'}
          sx={{ paddingTop: 6, textTransform: 'capitalize' }}
        >
          Conversation List
        </Typography>
      </Grid>

      <Grid container mb={'24px'} p={'6px'} spacing={2} xs={12} justifyContent='space-between'>
        <Grid item xs={3.5} justifyContent='flex-start'>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            sx={{
              background: '#F9FAFB',
              borderRadius: '23px',
              marginTop: '10px',
              '.MuiTab-root.Mui-selected': {
                background: '#9F84FF',
                color: '#fff',
                borderRadius: '23px',
                fontWeight: 500,
              },
              '.MuiTabs-flexContainer': {
                justifyContent: 'left',
                padding: '6px',
                borderRadius: '24px',
                border: '1px solid #F2F4F7',
                background: '#F9FAFB',
              },
            }}
            TabIndicatorProps={{
              style: {
                background: 'transparent',
              },
            }}
            centered
          >
            <Tab
              label={`Open Assigned (${
                conversationCount && conversationCount?.assigned.length ? conversationCount?.assigned[0] : 0
              })`}
            />
            <Tab
              label={`Unassigned (${
                conversationCount && conversationCount?.unassigned.length ? conversationCount?.unassigned[0] : 0
              })`}
            />
          </Tabs>
        </Grid>
        <Grid
          item
          xs={3}
          alignSelf={'flex-end'}
          justifySelf={'flex-end'}
          sx={{
            background: '#fff',
            border: '1px solid #F2F4F7',
            borderRadius: '8px',
            justifyContent: 'center',
            padding: '6px 16px',
          }}
        >
          <Typography variant='body1' sx={{ fontWeight: 700, paddingBottom: '6px' }}>
            Agent Status{' '}
          </Typography>
          <Grid container>
            <Grid item xs={6} sx={{ display: 'flex' }}>
              <CardMedia
                component='img'
                src={online}
                alt='image'
                sx={{
                  width: '16px',
                  height: '16px',
                  marginRight: '10px',
                }}
              />
              <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>Online </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, pl: '5px' }}>
                {' '}
                ({vmaStatus?.online.length ? vmaStatus?.online[0] : 0})
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex' }}>
              <CardMedia
                component='img'
                src={offline}
                alt='image'
                sx={{
                  width: '16px',
                  height: '16px',
                  marginRight: '10px',
                }}
              />
              <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>Offline </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, pl: '5px' }}>
                {' '}
                ({vmaStatus?.offline.length ? vmaStatus?.offline[0] : 0})
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container justifyContent='center' alignItems='center'>
        <Grid item xs={12} sx={{ backgroundColor: COLORS.White }}>
          <CustomDataGrid
            rows={conversations ? conversations : []}
            columns={selectedTab === 0 ? columns : unAssignColumns}
            dashBordKey={pageKey + '_' + selectedTab}
            userId={user.userId}
            setting={{ disableExport: !user?.permissions.includes('EXPORT_DATA') }}
          />
        </Grid>
        <PopupMenu
          anchorEl={anchorEl} 
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          loading={loading}
          items={groupDetails?.map((g: any) => ({
            label: g.groupName,
            onClick: () => handleJoinConversation(g),
            icon: g.activeConversation ? <img src={online} width={10} height={10} alt='online indicator' /> : null,
          }))}
          width={260}
        />
      </Grid>
      <PopupGroupMembers
        anchorEl={membersAnchorEl}
        open={Boolean(membersAnchorEl)}
        onClose={handleMembersClose}
        loading={membersLoading}
        members={members}
      />
    </Container>
  );
};
export default ConversationList;
